import React, { useEffect, useRef, useState } from 'react';
import classNames from 'classnames';

import {
  Icon,
  OutsideClickHandler,
  PaginationScroll,
  ProductsFiltering,
  SearchInput,
  useIsMobile,
  useRequestAbortController
} from 'components';
import SearchThumbnail from 'components/layout/SearchThumbnail';
import { notifyApiError } from 'components/layout/Toasts';

import { ProductsApi } from 'src/api';

import style from './ProductSearch.module.scss';

const QUERY_LIMIT = 25;

const ProductSearch = ({
  list = [],
  hiddenElements = [],
  setHiddenElements,
  onProductClick,
  hideQty,
  fullWidth,
  placeholder,
  scrollIntoViewOnActive
}) => {
  const isMobile = useIsMobile();
  const containerRef = useRef(null);

  const [page, setPage] = useState(0);
  const [querySearch, setQuerySearch] = useState();
  const [hasMore, setHasMore] = useState(true);
  const [products, setProducts] = useState([]);
  const [isActive, setIsActive] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedProducer, setSelectedProducer] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [isFilteringVisible, setIsFilteringVisible] = useState(false);
  const [wasDataCleared, setWasDataCleared] = useState(false);
  const [abortController, setNewController] = useRequestAbortController();

  const headerClasses = classNames(style.header, { [style.active]: isActive });
  const containerClasses = classNames(style.container, {
    [style.active]: isActive,
    [style.fullWidth]: fullWidth
  });
  const contentClasses = classNames(style.content, {
    [style.active]: isActive
  });

  const getData = async (page, clearData) => {
    const params = {
      page: page,
      perPage: QUERY_LIMIT,
      search: querySearch || undefined,
      categoryId: selectedCategory?.value,
      producerId: selectedProducer?.value
    };

    if (abortController) abortController.abort();
    const signal = setNewController();

    try {
      setIsLoading(true);
      const { data } = await ProductsApi.getProducts(params, signal);

      if (clearData || data.data.length === 0) {
        setWasDataCleared(true);
        setProducts(data.data);
        setHasMore(data.data.length < data.total);
      } else {
        setWasDataCleared(false);
        setProducts((prev) => [...prev, ...data.data]);
        setHasMore(data.data.length + products.length < data.total);
      }
    } catch (err) {
      notifyApiError(err);
    } finally {
      setIsLoading(false);
    }
  };

  const loadMoreResults = () => {
    if (!isLoading) {
      getData(page + 1);
      setPage(page + 1);
    }
  };

  const focusHandler = () => {
    setIsActive(true);
    if (scrollIntoViewOnActive && containerRef.current) {
      containerRef.current.scrollIntoView({
        behavior: 'auto',
        block: 'start',
        inline: 'start'
      });
    }
  };

  const clearHandler = () => {
    setIsActive(false);
  };

  useEffect(() => {
    if (setHiddenElements && list) {
      const arr = [];
      list.forEach((cat) => {
        cat.items.forEach((item) => {
          arr.push(item.id);
        });
      });

      setHiddenElements(arr);
    }
  }, [list, setHiddenElements]);

  useEffect(() => {
    const searchDelay = setTimeout(() => {
      getData(1, true);
      setPage(1);
    }, 400);

    return () => clearTimeout(searchDelay);
  }, [querySearch]);

  useEffect(() => {
    getData(1, true);
    setPage(1);
  }, [selectedProducer, selectedCategory]);

  useEffect(() => {
    setIsFilteringVisible(isMobile);
  }, [isMobile]);

  useEffect(() => {
    document.body.style.overflow = isActive ? 'hidden' : 'auto';
  }, [isActive]);

  useEffect(() => {
    return () => (document.body.style.overflow = 'auto');
  }, []);

  return (
    <div
      className={containerClasses}
      ref={containerRef}
    >
      <OutsideClickHandler onOutsideClick={clearHandler}>
        <div>
          <div className={headerClasses}>
            <SearchInput
              state={querySearch}
              setState={setQuerySearch}
              className={style.input}
              inputClasses={style.input}
              clearButtonVisible={isActive}
              onFocus={focusHandler}
              onClear={clearHandler}
              placeholder={placeholder}
              alwaysExpand
            />
            {isMobile && isActive && (
              <button
                className={classNames(style.mobileButton, {
                  [style.active]: !!selectedProducer || !!selectedCategory
                })}
                onClick={() => setIsFilteringVisible((prev) => !prev)}
              >
                <Icon
                  name={'filter'}
                  size={24}
                  fill={!!selectedProducer || !!selectedCategory ? '#0001DB' : '#0A1B2D'}
                />
              </button>
            )}
          </div>
          <div className={contentClasses}>
            <ProductsFiltering
              setSelectedProducer={setSelectedProducer}
              selectedProducer={selectedProducer}
              setSelectedCategory={setSelectedCategory}
              selectedCategory={selectedCategory}
              isFilteringVisible={isFilteringVisible}
            />
            <PaginationScroll
              data={products}
              hasMore={hasMore}
              page={page}
              loadMoreResults={loadMoreResults}
              isFilteringVisible={isFilteringVisible}
              wasDataCleared={wasDataCleared}
            >
              {products.map((item, index) => (
                <SearchThumbnail
                  onClick={(qty) => onProductClick(item, qty)}
                  productId={item.id}
                  closeSearch={clearHandler}
                  key={String(item.id) + index}
                  title={item.name}
                  pricesCount={item?.prices_count}
                  unitName={item.unit_name}
                  subtitle={item.producer}
                  thumbnail={item?.thumbnail || placeholder}
                  image={item?.image || placeholder}
                  hideElement={hiddenElements.includes(item.id)}
                  isLoading={isLoading && page === 1}
                  hideQty={hideQty}
                />
              ))}
            </PaginationScroll>
          </div>
        </div>
      </OutsideClickHandler>
    </div>
  );
};

export default ProductSearch;
